import { TextField } from '@mui/material';
import FormField from 'further-ui/components/FormField';
import { Column } from 'further-ui/components/Layout';
import { transactionType as rules } from 'further-ui/validations';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import useAccountSelectorOptions from 'hooks/form/useAccountSelectorOptions';
import AccountSelector from 'components/FormElements/AccountSelector';
import { forwardRef, useImperativeHandle } from 'react';

type FormValues = z.infer<typeof rules.CreateTransactionType>;

export type TransactionTypeFormContentRef = { submit: () => void };

export type TransactionTypeData = {
  id: string;
  name: string;
  accountId: string;
};

type Props = {
  onSubmit: (data: FormValues) => void;
  selectedAccountId?: string;
  transactionType?: TransactionTypeData;
};

const TransactionTypeFormContent = forwardRef<
  TransactionTypeFormContentRef,
  Props
>(({ onSubmit, selectedAccountId, transactionType }, ref) => {
  const { accountOptions } = useAccountSelectorOptions();
  const { control, handleSubmit } = useForm<FormValues>({
    resolver: zodResolver(rules.CreateTransactionType),
    defaultValues: {
      accountId: transactionType?.accountId ?? selectedAccountId,
      name: transactionType?.name ?? '',
    },
  });

  useImperativeHandle(ref, () => ({ submit: handleSubmit(onSubmit) }), [
    handleSubmit,
    onSubmit,
  ]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Column spacing="lg">
        <FormField label="Account">
          <Controller
            name="accountId"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <AccountSelector
                error={error}
                placeholder="Select account"
                {...field}
                accountOptions={accountOptions}
                disabled={!!selectedAccountId || !!transactionType}
              />
            )}
          />
        </FormField>
        <FormField label="Transaction type name">
          <Controller
            name="name"
            control={control}
            render={({ field: { ref, ...rest }, fieldState: { error } }) => (
              <TextField
                {...rest}
                inputRef={ref}
                placeholder="Enter transaction type name"
                size="small"
                error={!!error}
                helperText={error?.message}
                fullWidth
              />
            )}
          />
        </FormField>
      </Column>
    </form>
  );
});

TransactionTypeFormContent.displayName = 'TransactionTypeFormContent';

export default TransactionTypeFormContent;
